/**
 * Templates Management
 *
 * CycleTemplates are initialization blueprints — applied ONCE when a cycle is
 * created. They seed attributes (criteria), participants, and groups into the
 * cycle. After creation the cycle owns those values; changes to the template
 * have no effect on existing cycles.
 *
 * This is distinct from Workflows, which are runtime orchestration policy objects
 * that govern cycles throughout their entire lifecycle.
 *
 * Canon: "Workflow is always live. Templates are applied once."
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  LayoutTemplate,
  Archive,
  RefreshCw,
  X,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { Checkbox } from '../../../components/ds';
import {
  DISPLACEMENT_RULE_CHIP,
  DISPLACEMENT_RULE_HELP,
  DISPLACEMENT_RULE_LABEL,
} from '../../../lib/planning/vocabulary';
import {
  useCycleTemplateStore,
  selectActiveTemplates,
  CYCLE_TEMPLATE_SEEDS,
} from '../../../stores/prioritization/cycleTemplateStore';
import { useWorkflowStore } from '../../../stores/prioritization/workflowStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useGroupsStore } from '../../../stores/prioritization/groupsStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import type {
  CycleTemplate,
  TemplateAttributePreset,
  TemplateRangePreset,
  TemplateParticipantPreset,
  TemplateGroupPreset,
} from '../../../types/prioritization/cycleTemplate';

const RANGE_TYPE_OPTIONS: Array<{ value: TemplateRangePreset['type']; label: string; defaultOptions?: (string | number)[] }> = [
  { value: 'fibonacci', label: 'Fibonacci', defaultOptions: [1, 2, 3, 5, 8, 13, 21] },
  { value: 'tshirt', label: 'T-Shirt Sizes', defaultOptions: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
  { value: 'select', label: 'Low / Medium / High', defaultOptions: ['Low', 'Medium', 'High'] },
  { value: 'number', label: 'Numeric Range' },
  { value: 'custom', label: 'Custom Options' },
];

type EditState = {
  name: string;
  description: string;
  defaultAttributes: TemplateAttributePreset[];
  defaultParticipants: TemplateParticipantPreset[];
  defaultGroups: TemplateGroupPreset[];
  /** The displacement rule plan cycles start with (769). */
  displacementDiscipline: boolean;
  /** ID of the workflow that should point to this template (workflow.templateId). */
  linkedWorkflowId: string | null;
};

function emptyEdit(): EditState {
  return { name: '', description: '', defaultAttributes: [], defaultParticipants: [], defaultGroups: [], displacementDiscipline: false, linkedWorkflowId: null };
}

function templateToEdit(t: CycleTemplate, linkedWorkflowId: string | null = null): EditState {
  return {
    name: t.name,
    description: t.description ?? '',
    defaultAttributes: t.defaultAttributes,
    defaultParticipants: t.defaultParticipants,
    defaultGroups: t.defaultGroups,
    displacementDiscipline: t.displacementDiscipline,
    linkedWorkflowId,
  };
}

export default function Templates() {
  const templates = useCycleTemplateStore((s) => s.templates);
  const isLoading = useCycleTemplateStore((s) => s.isLoading);
  const loadError = useCycleTemplateStore((s) => s.loadError);
  const fetchTemplates = useCycleTemplateStore((s) => s.fetchTemplates);
  const createTemplate = useCycleTemplateStore((s) => s.createTemplate);
  const updateTemplate = useCycleTemplateStore((s) => s.updateTemplate);
  const archiveTemplate = useCycleTemplateStore((s) => s.archiveTemplate);
  const userId = useUserStore((s) => s.userId) ?? 'system';

  const allAttributes = useAttributesStore((s) => s.attributes) || [];
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  const allGroups = useGroupsStore((s) => s.groups) || [];
  const fetchGroups = useGroupsStore((s) => s.fetchFromSupabase);
  const { users: systemUsers } = useSystemUsers();

  const workflows = useWorkflowStore((s) => s.workflows);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);
  const updateWorkflow = useWorkflowStore((s) => s.updateWorkflow);
  const activeWorkflows = useMemo(() => workflows.filter((w) => w.status !== 'archived'), [workflows]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { fetchAttributes(); fetchGroups(); }, [fetchAttributes, fetchGroups]);
  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const activeTemplates = useMemo(() => selectActiveTemplates(templates), [templates]);

  // --- Create modal ---
  const [showCreate, setShowCreate] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createEdit, setCreateEdit] = useState<EditState>(emptyEdit());

  // --- Edit modal ---
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>(emptyEdit());
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (t: CycleTemplate) => {
    const linkedWorkflow = workflows.find((w) => w.templateId === t.id);
    setEditingId(t.id);
    setEditState(templateToEdit(t, linkedWorkflow?.id ?? null));
  };

  /** Apply workflow link changes: clear old, set new. */
  const applyWorkflowLink = async (templateId: string, prevWorkflowId: string | null, nextWorkflowId: string | null) => {
    if (prevWorkflowId === nextWorkflowId) return;
    // Cycle creation reads the column first and the configuration's copy second, so
    // both are written; otherwise an unlinked template comes back from the copy (S-43).
    const link = (workflowId: string, id: string | null) => {
      const configuration = workflows.find((w) => w.id === workflowId)?.configuration;
      return updateWorkflow(workflowId, {
        templateId: id,
        ...(configuration ? { configuration: { ...configuration, template_id: id } } : {}),
      });
    };
    if (prevWorkflowId) await link(prevWorkflowId, null);
    if (nextWorkflowId) await link(nextWorkflowId, templateId);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createEdit.name.trim() || createSubmitting) return;
    setCreateSubmitting(true);
    const newTemplate = await createTemplate({
      name: createEdit.name.trim(),
      description: createEdit.description.trim() || undefined,
      createdBy: userId,
      defaultAttributes: createEdit.defaultAttributes,
      defaultParticipants: createEdit.defaultParticipants,
      defaultGroups: createEdit.defaultGroups,
      displacementDiscipline: createEdit.displacementDiscipline,
    });
    if (newTemplate && createEdit.linkedWorkflowId) {
      await applyWorkflowLink(newTemplate.id, null, createEdit.linkedWorkflowId);
    }
    setCreateSubmitting(false);
    setShowCreate(false);
    setCreateEdit(emptyEdit());
  };

  const handleCreateFromSeed = async (seed: (typeof CYCLE_TEMPLATE_SEEDS)[number]) => {
    if (createSubmitting) return;
    setCreateSubmitting(true);
    await createTemplate({
      name: seed.name,
      description: seed.description,
      createdBy: userId,
      defaultAttributes: seed.defaultAttributes,
      defaultParticipants: seed.defaultParticipants,
      defaultGroups: seed.defaultGroups,
    });
    setCreateSubmitting(false);
  };

  const handleSaveEdit = async () => {
    if (!editingId || editSaving) return;
    setEditSaving(true);
    await updateTemplate(editingId, {
      name: editState.name.trim(),
      description: editState.description.trim() || undefined,
      defaultAttributes: editState.defaultAttributes,
      defaultParticipants: editState.defaultParticipants,
      defaultGroups: editState.defaultGroups,
      displacementDiscipline: editState.displacementDiscipline,
    });
    const prevLinkedWorkflow = workflows.find((w) => w.templateId === editingId);
    await applyWorkflowLink(editingId, prevLinkedWorkflow?.id ?? null, editState.linkedWorkflowId);
    setEditSaving(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<LayoutTemplate className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Templates"
        subtitle={
          <span>
            Initialization blueprints applied once at cycle creation — seeds attributes, participants, and groups.{' '}
            <Link to="/prioritization/settings/workflows" className="text-primary-600 hover:underline">
              Link to a workflow
            </Link>{' '}
            to apply automatically.
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {CYCLE_TEMPLATE_SEEDS.map((seed) => (
              <button
                key={seed.name}
                type="button"
                onClick={() => handleCreateFromSeed(seed)}
                disabled={createSubmitting}
                title={seed.description}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50"
              >
                <LayoutTemplate className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                {seed.name}
              </button>
            ))}
            <Link
              to="guided"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/30 hover:bg-primary-100 dark:hover:bg-primary-950/50 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Guided setup
            </Link>
            <button
              type="button"
              onClick={() => { setCreateEdit(emptyEdit()); setShowCreate(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" />
              New Template
            </button>
          </div>
        }
      />

      {loadError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 text-sm text-[var(--coral)]">
          {loadError}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-[var(--text-ter)] py-4">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading templates…</span>
        </div>
      ) : activeTemplates.length === 0 ? (
        <div className="bg-[var(--bg-raised)] border border-dashed border-[var(--rule)] p-10 text-center">
          <LayoutTemplate className="mx-auto h-8 w-8 text-[var(--text-ter)] mb-2" />
          <p className="text-sm font-medium text-[var(--text-sec)]">No templates yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Create one or use a preset from the header.</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
          {activeTemplates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface)]/60 transition-colors group">
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-[var(--text-pri)]">{t.name}</span>
                {t.description && (
                  <p className="mt-0.5 text-xs text-[var(--text-ter)] truncate">{t.description}</p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {t.defaultAttributes.length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--mustard)]/30 text-[var(--mustard)] border border-[var(--mustard)]/40 dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)] dark:border-[var(--mustard)]/40">
                      {t.defaultAttributes.length} attr{t.defaultAttributes.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {t.defaultParticipants.length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--slate)]/30 text-[var(--text-ter)] border border-[var(--slate)]/40 dark:bg-[var(--slate)]/30 dark:text-[var(--text-ter)] dark:border-[var(--slate)]/40">
                      {t.defaultParticipants.length} participant{t.defaultParticipants.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {t.defaultGroups.length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--slate)]/30 text-[var(--text-ter)] border border-[var(--slate)]/40 dark:bg-[var(--slate)]/30 dark:text-[var(--text-ter)] dark:border-[var(--slate)]/40">
                      {t.defaultGroups.length} group{t.defaultGroups.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {t.displacementDiscipline && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--coral)]/20 text-[var(--coral)] border border-[var(--coral)]/40">
                      {DISPLACEMENT_RULE_CHIP}
                    </span>
                  )}
                  {(() => {
                    const linked = workflows.find((w) => w.templateId === t.id);
                    return linked ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--green)]/30 text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]/40">
                        <GitBranch className="h-3 w-3" />
                        {linked.name}
                      </span>
                    ) : null;
                  })()}
                  {t.defaultAttributes.length === 0 && t.defaultParticipants.length === 0 && t.defaultGroups.length === 0 && !t.displacementDiscipline && !workflows.find((w) => w.templateId === t.id) && (
                    <span className="text-xs text-[var(--text-ter)] italic">No presets configured</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => openEdit(t)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => archiveTemplate(t.id)}
                  title="Archive"
                  className="p-1.5 text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
                >
                  <Archive className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {templates.some((t) => t.status === 'archived') && (
        <details className="mt-2">
          <summary className="text-xs text-[var(--text-ter)] cursor-pointer hover:text-[var(--text-sec)] select-none">Show archived</summary>
          <div className="mt-2 bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
            {templates
              .filter((t) => t.status === 'archived')
              .map((t) => (
                <div key={t.id} className="flex items-center justify-between px-4 py-2.5 text-[var(--text-ter)]">
                  <span className="text-sm">{t.name}</span>
                  <span className="text-xs">Archived</span>
                </div>
              ))}
          </div>
        </details>
      )}

      {/* Edit modal */}
      {editingId && (
        <TemplateEditModal
          title="Edit Template"
          state={editState}
          onChange={setEditState}
          allAttributes={allAttributes}
          systemUsers={systemUsers}
          allGroups={allGroups}
          activeWorkflows={activeWorkflows}
          onSave={handleSaveEdit}
          onClose={() => setEditingId(null)}
          saving={editSaving}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <TemplateEditModal
          title="Create Template"
          state={createEdit}
          onChange={setCreateEdit}
          allAttributes={allAttributes}
          systemUsers={systemUsers}
          allGroups={allGroups}
          activeWorkflows={activeWorkflows}
          onSave={handleCreate}
          onClose={() => { setShowCreate(false); setCreateEdit(emptyEdit()); }}
          saving={createSubmitting}
          isCreate
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplateEditModal
// ---------------------------------------------------------------------------

/**
 * Prominent attribute picker: workspace attributes as clickable chips first,
 * with a custom-name text link as the secondary option.
 */
function AttributePicker({
  allAttributes,
  usedKeys,
  onAdd,
}: {
  allAttributes: { key: string; label: string }[];
  usedKeys: string[];
  onAdd: (key: string, customLabel?: string) => void;
}) {
  const [showCustom, setShowCustom] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const available = allAttributes.filter((a) => !usedKeys.includes(a.key));

  function addCustom() {
    const label = customLabel.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    onAdd(key, label);
    setCustomLabel('');
    setShowCustom(false);
  }

  return (
    <div className="space-y-3">
      {available.length > 0 ? (
        <div>
          <p className="text-xs text-[var(--text-ter)] mb-2">
            {usedKeys.length === 0 ? 'Select criteria to include:' : 'Add more:'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {available.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => onAdd(a.key)}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] text-sm font-medium text-[var(--text-pri)] hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <Plus className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                {a.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        usedKeys.length === 0 && (
          <p className="text-xs text-[var(--text-ter)] italic">No attributes defined in this workspace yet.</p>
        )
      )}

      <div className="pt-1 border-t border-[var(--rule)]">
        {!showCustom ? (
          <button
            type="button"
            onClick={() => setShowCustom(true)}
            className="text-xs text-primary-600 hover:underline"
          >
            + Add a custom criterion not in your workspace
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              placeholder="e.g. Customer impact"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } if (e.key === 'Escape') setShowCustom(false); }}
              className="flex-1 text-sm px-3 py-1.5 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 bg-[var(--bg-raised)] text-[var(--text-pri)]"
            />
            <button type="button" disabled={!customLabel.trim()} onClick={addCustom}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40">
              Add
            </button>
            <button type="button" onClick={() => setShowCustom(false)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface TemplateEditModalProps {
  title: string;
  state: EditState;
  onChange: React.Dispatch<React.SetStateAction<EditState>>;
  allAttributes: { key: string; label: string }[];
  systemUsers: { id: string; name?: string | null; email?: string | null }[];
  allGroups: { id: string; displayName?: string | null; name?: string | null }[];
  activeWorkflows: { id: string; name: string; templateId?: string | null }[];
  onSave: (e: React.FormEvent) => void | Promise<void>;
  onClose: () => void;
  saving: boolean;
  isCreate?: boolean;
}

function TemplateEditModal({
  title,
  state,
  onChange,
  allAttributes,
  systemUsers,
  allGroups,
  activeWorkflows,
  onSave,
  onClose,
  saving,
  isCreate,
}: TemplateEditModalProps) {
  const [expandedSection, setExpandedSection] = useState<'attributes' | 'participants' | 'groups' | null>('attributes');

  const addAttribute = (key: string, _customLabel?: string) => {
    if (!key || state.defaultAttributes.some((a) => a.attributeKey === key)) return;
    onChange((s) => ({ ...s, defaultAttributes: [...s.defaultAttributes, { attributeKey: key }] }));
  };
  const removeAttribute = (key: string) =>
    onChange((s) => ({ ...s, defaultAttributes: s.defaultAttributes.filter((a) => a.attributeKey !== key) }));
  const updateAttribute = (key: string, updates: Partial<TemplateAttributePreset>) =>
    onChange((s) => ({
      ...s,
      defaultAttributes: s.defaultAttributes.map((a) => (a.attributeKey === key ? { ...a, ...updates } : a)),
    }));

  const addParticipant = (userId: string) => {
    if (!userId || state.defaultParticipants.some((p) => p.userId === userId)) return;
    const user = systemUsers.find((u) => u.id === userId);
    onChange((s) => ({
      ...s,
      defaultParticipants: [...s.defaultParticipants, { userId, displayName: user?.name || user?.email || userId }],
    }));
  };
  const removeParticipant = (userId: string) =>
    onChange((s) => ({ ...s, defaultParticipants: s.defaultParticipants.filter((p) => p.userId !== userId) }));

  const addGroup = (groupId: string) => {
    if (!groupId || state.defaultGroups.some((g) => g.groupId === groupId)) return;
    const group = allGroups.find((g) => g.id === groupId);
    onChange((s) => ({
      ...s,
      defaultGroups: [...s.defaultGroups, { groupId, displayName: group?.displayName || group?.name || groupId }],
    }));
  };
  const removeGroup = (groupId: string) =>
    onChange((s) => ({ ...s, defaultGroups: s.defaultGroups.filter((g) => g.groupId !== groupId) }));

  const Section = ({
    id, label, count, children,
  }: { id: 'attributes' | 'participants' | 'groups'; label: string; count: number; children: React.ReactNode }) => {
    const open = expandedSection === id;
    return (
      <div className="border border-[var(--rule)] overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 bg-[var(--surface)] hover:bg-[var(--hover-tint)] text-sm font-medium text-[var(--text-pri)]"
          onClick={() => setExpandedSection(open ? null : id)}
        >
          <span>{label}</span>
          <span className="flex items-center gap-2">
            {count > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg-raised)]/10 border border-white/20 text-[var(--text-ter)]">
                {count}
              </span>
            )}
            {open ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />}
          </span>
        </button>
        {open && <div className="p-4 space-y-3">{children}</div>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">{title}</h2>
        <p className="text-xs text-[var(--text-ter)] mb-4">
          Presets are applied once at cycle creation. After that the cycle owns these values.
        </p>

        <form onSubmit={onSave}>
          {/* Name + description */}
          <div className="space-y-3 mb-5">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name *</label>
              <input
                type="text"
                value={state.name}
                onChange={(e) => onChange((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Impact / Effort"
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500"
                required
                autoFocus={isCreate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
              <textarea
                value={state.description}
                onChange={(e) => onChange((s) => ({ ...s, description: e.target.value }))}
                placeholder="Optional — what this template is for"
                rows={2}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                <span className="flex items-center gap-1.5">
                  <GitBranch className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                  Linked Workflow
                </span>
              </label>
              <select
                value={state.linkedWorkflowId ?? ''}
                onChange={(e) => onChange((s) => ({ ...s, linkedWorkflowId: e.target.value || null }))}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 bg-[var(--bg-raised)] text-[var(--text-pri)] text-sm"
              >
                <option value="">— None —</option>
                {activeWorkflows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}{w.templateId && w.templateId !== '' && w.id !== state.linkedWorkflowId ? ' (has template)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                When a cycle is created with this workflow, this template will be applied automatically.
              </p>
            </div>
          </div>

          {/* Initialization presets */}
          <div className="space-y-2 mb-5">
            {/* Attributes */}
            <Section id="attributes" label="Attributes (Criteria)" count={state.defaultAttributes.length}>
              {state.defaultAttributes.map((preset) => {
                const attr = allAttributes.find((a) => a.key === preset.attributeKey);
                return (
                  <div key={preset.attributeKey} className="p-3 bg-[var(--surface)] border border-[var(--rule)] space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[var(--text-pri)] min-w-[100px]">
                        {attr?.label ?? preset.attributeKey}
                      </span>
                      <label className="flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
                        <span>Range</span>
                        <select
                          value={preset.defaultRange?.type ?? ''}
                          onChange={(e) => {
                            const selectedType = e.target.value as TemplateRangePreset['type'] | '';
                            if (!selectedType) {
                              updateAttribute(preset.attributeKey, { defaultRange: null });
                              return;
                            }
                            const opt = RANGE_TYPE_OPTIONS.find((r) => r.value === selectedType);
                            const range: TemplateRangePreset = { type: selectedType };
                            if (opt?.defaultOptions) range.options = [...opt.defaultOptions];
                            if (selectedType === 'number') { range.min = 1; range.max = 10; range.step = 1; }
                            updateAttribute(preset.attributeKey, { defaultRange: range });
                          }}
                          className="text-xs px-2 py-1 border border-[var(--rule)] rounded bg-[var(--bg-raised)] text-[var(--text-sec)]"
                        >
                          <option value="">No default</option>
                          {RANGE_TYPE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-1 text-xs text-[var(--text-sec)] ml-auto">
                        <input
                          type="checkbox"
                          checked={preset.required ?? false}
                          onChange={(e) => updateAttribute(preset.attributeKey, { required: e.target.checked })}
                          className="rounded border-[var(--rule)]"
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        onClick={() => removeAttribute(preset.attributeKey)}
                        className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Range detail row */}
                    {preset.defaultRange && (
                      <div className="flex items-start gap-2 pt-1 border-t border-[var(--rule)]">
                        <div className="flex-1 space-y-1.5">
                          {preset.defaultRange.type === 'number' && (
                            <div className="flex items-center gap-2 text-xs">
                              <label className="text-[var(--text-ter)]">Min</label>
                              <input type="number" value={preset.defaultRange.min ?? 1}
                                onChange={(e) => updateAttribute(preset.attributeKey, { defaultRange: { ...preset.defaultRange!, min: Number(e.target.value) } })}
                                className="w-16 px-2 py-1 border border-[var(--rule)] rounded" />
                              <label className="text-[var(--text-ter)]">Max</label>
                              <input type="number" value={preset.defaultRange.max ?? 10}
                                onChange={(e) => updateAttribute(preset.attributeKey, { defaultRange: { ...preset.defaultRange!, max: Number(e.target.value) } })}
                                className="w-16 px-2 py-1 border border-[var(--rule)] rounded" />
                              <label className="text-[var(--text-ter)]">Step</label>
                              <input type="number" value={preset.defaultRange.step ?? 1}
                                onChange={(e) => updateAttribute(preset.attributeKey, { defaultRange: { ...preset.defaultRange!, step: Number(e.target.value) } })}
                                className="w-16 px-2 py-1 border border-[var(--rule)] rounded" />
                            </div>
                          )}
                          {(preset.defaultRange.type === 'select' || preset.defaultRange.type === 'custom') && (
                            <div className="text-xs">
                              <label className="text-[var(--text-ter)] block mb-1">Options (comma-separated)</label>
                              <input
                                type="text"
                                value={(preset.defaultRange.options ?? []).join(', ')}
                                onChange={(e) => {
                                  const opts = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                                  updateAttribute(preset.attributeKey, { defaultRange: { ...preset.defaultRange!, options: opts } });
                                }}
                                placeholder="e.g. Low, Medium, High"
                                className="w-full px-2 py-1 border border-[var(--rule)] rounded"
                              />
                            </div>
                          )}
                          {(preset.defaultRange.type === 'fibonacci' || preset.defaultRange.type === 'tshirt') && (
                            <p className="text-xs text-[var(--text-ter)]">{preset.defaultRange.options?.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <AttributePicker
                allAttributes={allAttributes}
                usedKeys={state.defaultAttributes.map((a) => a.attributeKey)}
                onAdd={addAttribute}
              />
              <p className="text-xs text-[var(--text-ter)]">These criteria are seeded into tracks when a cycle is created from this template.</p>
            </Section>

            {/* Participants */}
            <Section id="participants" label="Default Participants" count={state.defaultParticipants.length}>
              {state.defaultParticipants.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {state.defaultParticipants.map((p) => (
                    <span key={p.userId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs border border-[var(--slate)]">
                      {p.displayName || p.userId.slice(0, 8)}
                      <button type="button" onClick={() => removeParticipant(p.userId)} className="text-[var(--text-sec)] hover:text-[var(--coral)]">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select
                value=""
                onChange={(e) => { if (e.target.value) addParticipant(e.target.value); }}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] text-sm"
              >
                <option value="">+ Add participant…</option>
                {systemUsers
                  .filter((u) => !state.defaultParticipants.some((p) => p.userId === u.id))
                  .map((u) => <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>)}
              </select>
              <p className="text-xs text-[var(--text-ter)]">These users will be added to cycles created from this template.</p>
            </Section>

            {/* Groups */}
            <Section id="groups" label="Default Groups" count={state.defaultGroups.length}>
              {state.defaultGroups.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {state.defaultGroups.map((g) => (
                    <span key={g.groupId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs border border-[var(--slate)]">
                      {g.displayName || g.groupId.slice(0, 8)}
                      <button type="button" onClick={() => removeGroup(g.groupId)} className="text-[var(--text-sec)] hover:text-[var(--coral)]">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select
                value=""
                onChange={(e) => { if (e.target.value) addGroup(e.target.value); }}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] text-sm"
              >
                <option value="">+ Add group…</option>
                {allGroups
                  .filter((g) => !state.defaultGroups.some((d) => d.groupId === g.id))
                  .map((g) => <option key={g.id} value={g.id}>{g.displayName || g.name || g.id}</option>)}
              </select>
              <p className="text-xs text-[var(--text-ter)]">These groups will be added to cycles created from this template.</p>
            </Section>

            {/* Planning rule (owner ruling #12, migration 769) */}
            <div className="border border-[var(--rule)] px-3 py-3">
              <Checkbox
                checked={state.displacementDiscipline}
                onChange={(e) => onChange((s) => ({ ...s, displacementDiscipline: e.target.checked }))}
                label={DISPLACEMENT_RULE_LABEL}
                description={DISPLACEMENT_RULE_HELP}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !state.name.trim()}
              className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
            </button>
          </div>
        </form>

        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
